import React from 'react';

const ParkAndRideSchema = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://airportparkingdeals.co.uk/#organization",
        "name": "Airport Parking Deals",
        "url": "https://airportparkingdeals.co.uk",
        "logo": {
          "@type": "ImageObject",
          "url": "https://airportparkingdeals.co.uk/logo.png"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+44-800-000-0000",
          "contactType": "Customer Service",
          "areaServed": "GB",
          "availableLanguage": "English"
        },
        "sameAs": [
          "https://www.facebook.com/airportparkingdeals",
          "https://twitter.com/airportparking"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://airportparkingdeals.co.uk/#website",
        "url": "https://airportparkingdeals.co.uk",
        "name": "Airport Parking Deals",
        "publisher": {
          "@id": "https://airportparkingdeals.co.uk/#organization"
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://airportparkingdeals.co.uk/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "WebPage",
        "@id": "https://airportparkingdeals.co.uk/park-and-ride-parking#webpage",
        "url": "https://airportparkingdeals.co.uk/park-and-ride-parking",
        "name": "Park & Ride Airport Parking | How It Works & Money-Saving Tips | Airport Parking Deals",
        "isPartOf": {
          "@id": "https://airportparkingdeals.co.uk/#website"
        },
        "about": {
          "@id": "https://airportparkingdeals.co.uk/park-and-ride-parking#service"
        },
        "description": "Your guide to affordable Park & Ride airport parking. Discover how it works, why it's the budget-friendly choice, and how to book the best secure off-airport parking.",
        "breadcrumb": {
          "@id": "https://airportparkingdeals.co.uk/park-and-ride-parking#breadcrumb"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://airportparkingdeals.co.uk/park-and-ride-parking#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://airportparkingdeals.co.uk"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Park & Ride Parking",
            "item": "https://airportparkingdeals.co.uk/park-and-ride-parking"
          }
        ]
      },
      {
        "@type": "Service",
        "@id": "https://airportparkingdeals.co.uk/park-and-ride-parking#service",
        "serviceType": "Park & Ride Airport Parking",
        "name": "Park & Ride Airport Parking Services",
        "description": "Affordable Park & Ride airport parking with secure off-airport compounds and complimentary shuttle transfers. The budget-friendly choice for stress-free travel.",
        "provider": {
          "@id": "https://airportparkingdeals.co.uk/#organization"
        },
        "areaServed": {
          "@type": "Country",
          "name": "United Kingdom"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Park & Ride Parking Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Secure Off-Airport Parking",
                "description": "Park in secure, CCTV-monitored facilities with Park Mark® security awards"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Complimentary Shuttle Service",
                "description": "Frequent transfers running every 10-15 minutes to your terminal"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Budget-Friendly Rates",
                "description": "Most cost-effective airport parking option with great value"
              }
            }
          ]
        },
        "termsOfService": "https://airportparkingdeals.co.uk/terms-and-conditions",
        "audience": {
          "@type": "Audience",
          "audienceType": "Budget-conscious travelers and families"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How does Park & Ride airport parking work?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Park & Ride works in 3 simple steps: 1) Drive to a secure off-airport car park and park in a designated bay, 2) Catch the complimentary shuttle bus to your terminal (runs every 10-15 minutes), 3) On return, take the shuttle back and drive straight home."
            }
          },
          {
            "@type": "Question",
            "name": "Is Park & Ride parking secure?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, Park & Ride facilities feature CCTV monitoring, perimeter fencing, regular patrols, and many hold Park Mark® security awards for complete peace of mind."
            }
          },
          {
            "@type": "Question",
            "name": "When should I book Park & Ride parking?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Book as early as you book your flights for the best prices. Early booking is crucial during school holidays and peak summer months when affordable car parks sell out first. Booking months in advance can save up to 60%."
            }
          },
          {
            "@type": "Question",
            "name": "What are the benefits of Park & Ride parking?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Park & Ride offers great value, secure compounds with CCTV, hassle-free experience with no terminal charges, and family-friendly shuttle buses with luggage space. It's the most cost-effective option for all trip lengths."
            }
          }
        ]
      },
      {
        "@type": "HowTo",
        "name": "How to Use Park & Ride Airport Parking",
        "description": "Step-by-step guide to using Park & Ride airport parking service",
        "step": [
          {
            "@type": "HowToStep",
            "position": 1,
            "name": "Park & Secure",
            "text": "Drive to the secure off-airport car park. Check in at the barrier using your booking reference and park your car in a designated bay.",
            "image": "https://airportparkingdeals.co.uk/images/park-and-secure.jpg"
          },
          {
            "@type": "HowToStep",
            "position": 2,
            "name": "Catch the Shuttle",
            "text": "Head to the clearly marked bus stop. A frequent complimentary shuttle bus runs every 10-15 minutes directly to your terminal.",
            "image": "https://airportparkingdeals.co.uk/images/catch-shuttle.jpg"
          },
          {
            "@type": "HowToStep",
            "position": 3,
            "name": "Return & Collect",
            "text": "On your return, catch the shuttle bus from the designated airport stop back to the car park. Hop in your car and drive straight home.",
            "image": "https://airportparkingdeals.co.uk/images/return-collect.jpg"
          }
        ]
      },
      {
        "@type": "LocalBusiness",
        "name": "Airport Parking Deals - Park & Ride Service",
        "description": "Affordable park and ride airport parking services across UK airports",
        "image": "https://airportparkingdeals.co.uk/images/park-ride.jpg",
        "priceRange": "£-££",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "GB"
        },
        "telephone": "+44-800-000-0000",
        "url": "https://airportparkingdeals.co.uk/park-and-ride-parking"
      }
    ]
  };

  return (
    <script 
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} 
    />
  );
};

export default ParkAndRideSchema;

